"use client";

import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Stethoscope, GraduationCap, Clock, ChevronRight, CalendarCheck, Activity } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const specialists = [{
  name: "Dr Zano Tyrannis",
  role: "Chiropractor · Founder · 20+ years",
  image: "https://bodywerks.com.cy/wp-content/uploads/2026/03/image05.png",
  bio: "With over two decades of experience in chiropractic care, healthcare leadership and wellness innovation, Dr Tyrannis brings clinical expertise, compassion and a holistic, patient-centred approach to Bodywerks in Limassol and Nicosia.",
  qualifications: ["Doctor of Chiropractic", "M.Tech Chiro", "M.Sc Med", "MBA", "Author of The Back Pain Blueprint"],
  availability: "Limassol: Mon, Wed 16:00-19:30 · Tue, Thu 09:30-12:30 · Fri 15:00-18:30 · Nicosia by appointment",
  specialties: ["Back & Neck Pain", "Sciatica & Disc Problems", "Acupuncture & Dry Needling"]
}];
export default function SandtonSpecialists() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="53091" id="meet-your-practitioners" className="bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      <h1 className="sr-only">Our Story</h1>
      {}
      <div className="bg-accent/10 absolute -top-24 -right-24 h-96 w-96 rounded-lg blur-3xl" />
      <div className="bg-primary/10 absolute top-1/2 -left-24 h-64 w-64 rounded-lg blur-3xl" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div ref={headerRef} className={cn("mx-auto mb-16 max-w-2xl text-center transition-all duration-700 ease-out", isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
          <h2 className="text-primary mb-4 text-3xl font-medium tracking-tight sm:text-4xl font-serif">
            Meet Your Practitioners
          </h2>
          <p className="text-secondary text-lg font-sans">
            Led by Dr Zano Tyrannis, Doctor of Chiropractic with over 20 years of experience and author of The Back Pain Blueprint.
          </p>
        </div>

        {}
        <div ref={gridRef} className={specialists.length > 1 ? "grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4" : "grid grid-cols-1 gap-8 max-w-xs mx-auto"}>
          {specialists.map((specialist, i) => <Dialog key={specialist.name}>
              <div data-index={i} className={cn("group relative flex flex-col transition-all duration-700 ease-out", isGridVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")} style={{
            transitionDelay: `${i * 100}ms`
          }}>
                <Card className="border-border/50 bg-card/40 hover:border-accent/20 hover:shadow-xl rounded-2xl h-full overflow-hidden border transition-all duration-500 hover:-translate-y-1 backdrop-blur-sm">
                  {}
                  <div className="bg-accent/5 relative aspect-[3/4] overflow-hidden">
                    <img src={specialist.image} alt={specialist.name} className="h-full w-full object-cover object-top transition-all duration-700 filter grayscale-[20%] group-hover:scale-105 group-hover:grayscale-0" />
                    <div className="bg-gradient-to-t from-background/90 to-transparent absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  </div>

                  {}
                  <CardHeader className="relative z-10 pb-2 text-center">
                    <CardTitle className="text-primary text-xl font-medium font-serif">
                      {specialist.name}
                    </CardTitle>
                    <CardDescription className="text-accent font-medium font-sans">
                      {specialist.role}
                    </CardDescription>
                  </CardHeader>

                  <CardFooter className="relative z-10 flex justify-center pb-6 pt-0">
                    <DialogTrigger asChild>
                      <Button variant="link" className="text-secondary hover:text-accent group/link h-auto p-0 text-sm font-medium font-sans">
                        View Credentials
                        <ChevronRight className="ml-1 h-3 w-3 transition-transform group-hover/link:translate-x-1" />
                      </Button>
                    </DialogTrigger>
                  </CardFooter>
                </Card>
              </div>

              {}
              <DialogContent className="sm:max-w-2xl rounded-2xl border-none bg-background/95 backdrop-blur-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-medium font-serif text-primary">
                    {specialist.name}
                  </DialogTitle>
                  <DialogDescription className="text-accent text-base font-medium font-sans">
                    {specialist.role}
                  </DialogDescription>
                </DialogHeader>

                <div className="mt-4 grid gap-6 sm:grid-cols-[1fr_1.5fr]">
                  {}
                  <div className="aspect-[3/4] overflow-hidden rounded-xl sm:aspect-auto">
                    <img src={specialist.image} alt={specialist.name} className="h-full w-full object-cover object-top" />
                  </div>

                  {}
                  <div className="flex flex-col gap-6">
                    <div className="space-y-2">
                      <h4 className="flex items-center gap-2 font-semibold text-primary font-sans">
                        <Activity className="text-accent h-4 w-4" />
                        Professional Bio
                      </h4>
                      <p className="text-secondary text-sm leading-relaxed font-sans">
                        {specialist.bio}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <h4 className="flex items-center gap-2 font-semibold text-primary font-sans">
                        <GraduationCap className="text-accent h-4 w-4" />
                        International Credentials
                      </h4>
                      <ul className="text-secondary list-inside list-disc text-sm font-sans">
                        {specialist.qualifications.map((qual, idx) => <li key={idx} data-index={idx}>
                            {qual}
                          </li>)}
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <h4 className="flex items-center gap-2 font-semibold text-primary font-sans">
                        <Stethoscope className="text-accent h-4 w-4" />
                        Core Specialties
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {specialist.specialties.map((spec, idx) => <span key={idx} data-index={idx} className="bg-accent/10 text-accent rounded-lg px-2.5 py-0.5 text-xs font-medium font-sans">
                            {spec}
                          </span>)}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="flex items-center gap-2 font-semibold text-primary font-sans">
                        <Clock className="text-accent h-4 w-4" />
                        Clinic Hours
                      </h4>
                      <p className="text-secondary text-sm font-sans">
                        {specialist.availability}
                      </p>
                    </div>

                    <div className="mt-auto pt-4">
                      <Button className="w-full sm:w-auto rounded-lg bg-accent hover:bg-accent/90 text-white shadow-lg transition-all duration-300 hover:scale-105" asChild>
                        <Link to="/ContactPage">
                          <CalendarCheck className="mr-2 h-4 w-4" />
                          Book Appointment
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>)}
        </div>
      </div>
    </section>;
}